import React, { useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import "./KSRTCPage.css";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

type ImageFormat = "png" | "jpeg";

interface ConvertedImage {
  fileName: string;
  dataUrl: string;
}

function KSRTCPage() {
  const history = useHistory();
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [images, setImages] = useState<ConvertedImage[]>([]);
  const [selectedImage, setSelectedImage] =
    useState<ConvertedImage | null>(null);
  const [converting, setConverting] = useState(false);
  const [progress, setProgress] = useState("");
  const [imageFormat, setImageFormat] = useState<ImageFormat>("png");

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const files = Array.from(e.target.files).filter(
          (f) => f.type === "application/pdf"
        );
        setPdfFiles(files);
        setImages([]);
      }
    },
    []
  );

  const convertPdfsToImages = useCallback(async () => {
    if (pdfFiles.length === 0) return;
    setConverting(true);
    setImages([]);
    const allImages: ConvertedImage[] = [];

    for (let fi = 0; fi < pdfFiles.length; fi++) {
      const file = pdfFiles[fi];
      const pdfName = file.name.replace(/\.pdf$/i, "");
      setProgress(
        `Processing file ${fi + 1} of ${pdfFiles.length}: ${file.name}`
      );

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      for (let pi = 1; pi <= pdf.numPages; pi++) {
        setProgress(
          `Processing file ${fi + 1}/${pdfFiles.length} — page ${pi}/${pdf.numPages}`
        );
        const page = await pdf.getPage(pi);
        const scale = 2;
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d")!;

        await page.render({ canvasContext: ctx, viewport }).promise;

        const mimeType = imageFormat === "jpeg" ? "image/jpeg" : "image/png";
        const quality = imageFormat === "jpeg" ? 0.7 : undefined;
        const ext = imageFormat === "jpeg" ? "jpg" : "png";
        const dataUrl = canvas.toDataURL(mimeType, quality);
        allImages.push({
          fileName: `${pdfName}_page_${pi}.${ext}`,
          dataUrl,
        });
      }
    }

    setImages(allImages);
    setConverting(false);
    setProgress("");
  }, [pdfFiles, imageFormat]);

  const downloadAsZip = useCallback(async () => {
    if (images.length === 0) return;
    const zip = new JSZip();

    for (const img of images) {
      const base64 = img.dataUrl.split(",")[1];
      zip.file(img.fileName, base64, { base64: true });
    }

    const blob = await zip.generateAsync({ type: "blob" });
    saveAs(blob, "ksrtc_pdf_images.zip");
  }, [images]);

  return (
    <div className="ksrtc-container">
      <div style={{ marginBottom: "15px" }}>
        <button onClick={() => history.push("/")}>← Back to Home</button>
      </div>
      <h1>KSRTC — PDF to Images Converter</h1>

      <div className="upload-section">
        <label className="upload-label">Select PDF files:</label>
        <input
          type="file"
          accept=".pdf"
          multiple
          onChange={handleFileChange}
        />
      </div>

      <div className="format-section">
        <label className="upload-label">Image format:</label>
        <div className="format-options">
          <label className="format-option">
            <input
              type="radio"
              name="format"
              value="png"
              checked={imageFormat === "png"}
              onChange={() => setImageFormat("png")}
            />
            PNG (Original quality)
          </label>
          <label className="format-option">
            <input
              type="radio"
              name="format"
              value="jpeg"
              checked={imageFormat === "jpeg"}
              onChange={() => setImageFormat("jpeg")}
            />
            JPEG (Compressed — smaller file size)
          </label>
        </div>
      </div>

      {pdfFiles.length > 0 && (
        <div className="file-list">
          <h3>Selected Files ({pdfFiles.length}):</h3>
          <ul>
            {pdfFiles.map((f, i) => (
              <li key={i}>{f.name}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="actions">
        <button
          onClick={convertPdfsToImages}
          disabled={pdfFiles.length === 0 || converting}
        >
          {converting ? "Converting..." : "Convert to Images"}
        </button>

        {images.length > 0 && (
          <button onClick={downloadAsZip} className="download-btn">
            Download as ZIP ({images.length} images)
          </button>
        )}
      </div>

      {progress && <p className="progress-text">{progress}</p>}

      {images.length > 0 && (
        <div className="preview-section">
          <h3>Preview ({images.length} images):</h3>
          <div className="image-grid">
            {images.map((img, i) => (
              <div
                key={i}
                className="image-card"
                onClick={() => setSelectedImage(img)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setSelectedImage(img);
                  }
                }}
              >
                <img src={img.dataUrl} alt={img.fileName} />
                <p>{img.fileName}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedImage && (
        <div
          className="image-viewer-overlay"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="image-viewer-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="image-viewer-close"
              onClick={() => setSelectedImage(null)}
            >
              Close
            </button>
            <img src={selectedImage.dataUrl} alt={selectedImage.fileName} />
            <p>{selectedImage.fileName}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default KSRTCPage;
